import { useEffect, useMemo, useState } from 'react';
import { useWindowSize } from '../../hooks/useWindowSize';
import Pill from '../Shared/Pill';
import RegistrationForm from '../RegistrationForm/RegistrationForm';
import FeatureBlockSection from '../FeatureBlockElements/FeatureBlockSection/FeatureBlockSection';
import FeatureBlockContent from '../FeatureBlockElements/FeatureBlockContent/FeatureBlockContent';
import HBMediaElement from '../honeybook-ui/HBMediaElement/HBMediaElement';
import { Trans, useTranslation } from 'react-i18next';
import { Intent, intents } from './intents';
import HBText from '../honeybook-ui/HBText/HBText';
import UtilsService from '../../Services/UtilsService';
import StorageService from '../../Services/StorageService';
import { CLICK_START_TRIAL_ADDITIONAL_DATA_STORAGE_KEY } from '../../constants/constants';
import { TAdditionalDataForClickStartTrialModules } from '../../types/util-types';

import './traditional-get-started-hero.scss';
import { AnalyticsEvents } from '../../Services/AnalyticsService';
import AnalyticsService from '../../Services/AnalyticsService';

const ROTATION_INTERVAL = 3500;
const BROAD_INTENT_VARIANT = 'broad_intent_2_traditional';

const intentsMap: Record<string, Intent> = {};
intents.forEach(intent => {
    intentsMap[intent.key] = intent;
});

const BroadIntentTraditionalHero = () => {
    const { t } = useTranslation();
    const { isMediumUp } = useWindowSize();
    const [selected, setSelected] = useState<string[]>([]);
    const [hovered, setHovered] = useState<string | null>(null);
    const [rotatingIndex, setRotatingIndex] = useState(0);
    const [lastHovered, setLastHovered] = useState<string | null>(null);

    const pool =
        selected.length > 0
            ? intents.filter(i => selected.includes(i.key))
            : intents;

    const keyToIndex = useMemo(() => {
        const map = new Map<string, number>();
        pool.forEach((intent, index) => {
            map.set(intent.key, index);
        });
        return map;
    }, [pool]);

    useEffect(() => {
        const additionalData =
            StorageService.getItem(
                CLICK_START_TRIAL_ADDITIONAL_DATA_STORAGE_KEY,
                'sessionStorage'
            ) || {};
        const additionalDataPerPath =
            additionalData[window.location.pathname] || {};
        const selectedIntents =
            additionalDataPerPath[
                TAdditionalDataForClickStartTrialModules
                    .BROAD_INTENT_GET_STARTED_HERO
            ];

        if (
            selectedIntents?.intent_selected &&
            selectedIntents.intent_selected !== 'none'
        ) {
            setSelected(selectedIntents.intent_selected.split(','));
        }
        UtilsService.addAdditionalDataToClickStartTrial(
            {
                intent_selected: selectedIntents?.intent_selected || 'none',
                intent_selected_count:
                    selectedIntents?.intent_selected.split(',').length || 0,
                broad_intent_get_started_hero: BROAD_INTENT_VARIANT
            },
            TAdditionalDataForClickStartTrialModules.BROAD_INTENT_GET_STARTED_HERO
        );
    }, []);

    useEffect(() => {
        let intervalId: NodeJS.Timeout | null = null;

        if (hovered) {
            setLastHovered(hovered);
            const index = keyToIndex.get(hovered) ?? 0;
            setRotatingIndex(index);
            return;
        }

        if (!hovered && lastHovered) {
            const timeoutId = setTimeout(() => {
                const lastIndex = keyToIndex.get(lastHovered) ?? 0;
                setRotatingIndex(lastIndex);
                setLastHovered(null);
            }, 300);

            return () => {
                if (timeoutId) clearTimeout(timeoutId);
            };
        }

        if (!hovered && !lastHovered && pool.length > 1) {
            intervalId = setInterval(() => {
                setRotatingIndex(idx => (idx + 1) % pool.length);
            }, ROTATION_INTERVAL);
        }

        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [hovered, selected, lastHovered, pool, keyToIndex]);

    useEffect(() => {
        setRotatingIndex(0);
    }, [pool.length]);

    let displayIntent: Intent | undefined;
    if (hovered && intentsMap[hovered]) {
        displayIntent = intentsMap[hovered];
    } else if (selected.length > 0) {
        const lastSelectedKey = selected[selected.length - 1];
        displayIntent = intentsMap[lastSelectedKey];
    } else if (pool.length > 0) {
        displayIntent = pool[rotatingIndex % pool.length];
    } else {
        displayIntent = intents[0];
    }

    const handlePillClick = (key: string) => {
        const isEnabled = selected.includes(key);
        AnalyticsService.trackClick(AnalyticsEvents.intent_chip, {
            intent_selected: key,
            broad_intent_get_started_hero: BROAD_INTENT_VARIANT,
            toggled_to: isEnabled ? 'inactive' : 'active'
        });
        const updatedSelected = selected.includes(key)
            ? selected.filter(k => k !== key)
            : [...selected, key];

        const sortedSelected = [...updatedSelected].sort();

        setSelected(sortedSelected);

        UtilsService.addAdditionalDataToClickStartTrial(
            {
                intent_selected: sortedSelected.join() || 'none',
                intent_selected_count: sortedSelected.length
            },
            TAdditionalDataForClickStartTrialModules.BROAD_INTENT_GET_STARTED_HERO
        );
    };

    const handlePillHover = (key: string | null) => setHovered(key);

    return (
        <FeatureBlockSection customClass={'broad-intent-hero--traditional'}>
            <FeatureBlockContent customClass="broad-intent-hero--traditional__content">
                <div className="broad-intent-hero--traditional__text-controls">
                    <div className="broad-intent-hero--traditional__title">
                        <HBText type="title-l" tag="h1">
                            <Trans
                                t={t}
                                i18nKey={
                                    'broadIntentContent.hero.traditional.title'
                                }
                            >
                                <span className="serif-italic" />
                            </Trans>
                        </HBText>
                    </div>
                    <HBText
                        customClass="subtitle"
                        type="body-l"
                        tag="h2"
                        children={
                            isMediumUp
                                ? 'broadIntentContent.hero.traditional.subtitle.web'
                                : 'broadIntentContent.hero.traditional.subtitle.mobile'
                        }
                    />
                    <div className="broad-intent-hero--traditional__pills">
                        {intents.map(intent => (
                            <Pill
                                key={intent.key}
                                label={t(intent.labelKey)}
                                active={selected.includes(intent.key)}
                                onClick={() => handlePillClick(intent.key)}
                                onMouseEnter={() => handlePillHover(intent.key)}
                                onMouseLeave={() => handlePillHover(null)}
                                className="broad-intent-hero--traditional__pill"
                                size={isMediumUp ? 'medium' : 'small'}
                            >
                                <span className="plus">+</span>{' '}
                            </Pill>
                        ))}
                    </div>
                    <RegistrationForm
                        source={BROAD_INTENT_VARIANT}
                        buttonText={t(
                            'broadIntentContent.hero.traditional.cta'
                        )}
                        icon="nx-arrow-line-right-16"
                        buttonStyle="primary-rebrand"
                    />
                </div>
                <div className="broad-intent-hero--traditional__intent-img__container">
                    {displayIntent && (
                        <HBMediaElement
                            key={displayIntent.key}
                            name={displayIntent.img}
                            alt={t(displayIntent.labelKey)}
                            customClass="broad-intent-hero--traditional__intent-img"
                        />
                    )}
                </div>
            </FeatureBlockContent>
        </FeatureBlockSection>
    );
};

export default BroadIntentTraditionalHero;
